import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "@mindstonehq/ui";
import { BugIcon, ImageIcon, ShieldAlertIcon, VideoIcon, XIcon } from "lucide-react";
import { graphql } from "~/graphql/codegen";

const SUBMIT_CONTROLLED_BUG_REPORT = graphql(`
  mutation SubmitControlledBugReport($input: ControlledBugReportInput!) {
    submitControlledBugReport(input: $input) {
      environment
      status
      issueIdentifier
      issueUrl
      duplicateTitle
      slackDelivered
    }
  }
`);

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;
const ALLOWED_SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_RECORDING_BYTES = 15 * 1024 * 1024;
const ALLOWED_RECORDING_TYPES = ["video/mp4", "video/webm"];

type Screenshot = {
  base64: string;
  contentType: "image/png" | "image/jpeg" | "image/webp";
  name: string;
  previewUrl: string;
};

type Recording = {
  base64: string;
  contentType: "video/mp4" | "video/webm";
  name: string;
  previewUrl: string;
};

// Checks the file's type and size, then reads it as a data URL. Rejects with
// "type", "size" or "read" so each field can show its own message.
function readEvidenceFile(
  file: File,
  allowedTypes: string[],
  maxBytes: number,
): Promise<{ base64: string; dataUrl: string }> {
  return new Promise((resolve, reject) => {
    if (!allowedTypes.includes(file.type)) {
      reject(new Error("type"));
      return;
    }
    if (file.size > maxBytes) {
      reject(new Error("size"));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read"));
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      const base64 = dataUrl.split(",", 2)[1] ?? "";
      if (!base64) {
        reject(new Error("read"));
        return;
      }
      resolve({ base64, dataUrl });
    };
    reader.readAsDataURL(file);
  });
}

async function readScreenshot(file: File): Promise<Screenshot> {
  const { base64, dataUrl } = await readEvidenceFile(file, ALLOWED_SCREENSHOT_TYPES, MAX_SCREENSHOT_BYTES);
  return {
    base64,
    contentType: file.type as Screenshot["contentType"],
    name: file.name,
    previewUrl: dataUrl,
  };
}

// Leaves the preview URL to the caller, which creates it only once it knows the
// read is still wanted, so a stale read never leaves an object URL behind.
async function readRecording(file: File): Promise<Omit<Recording, "previewUrl">> {
  const { base64 } = await readEvidenceFile(file, ALLOWED_RECORDING_TYPES, MAX_RECORDING_BYTES);
  return {
    base64,
    contentType: file.type as Recording["contentType"],
    name: file.name,
  };
}

function errorReason(error: unknown) {
  return error instanceof Error ? error.message : "read";
}

export function ReportBugDialog({
  open,
  onOpenChange,
  mindspaceId,
  environment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mindspaceId: string;
  environment: string;
}) {
  const { t } = useLingui();
  const [title, setTitle] = useState("");
  const [actualBehavior, setActualBehavior] = useState("");
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [reproductionSteps, setReproductionSteps] = useState("");
  const [screenshot, setScreenshot] = useState<Screenshot | null>(null);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [fileError, setFileError] = useState("");
  const [recordingError, setRecordingError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState<{
    status: string;
    issueIdentifier: string;
    issueUrl: string;
    duplicateTitle: string;
  } | null>(null);
  const submissionGeneration = useRef(0);
  // Bumped on reset and unmount: a file read that finishes after either is dropped.
  const fileGeneration = useRef(0);
  const [submitReport, { loading }] = useMutation(SUBMIT_CONTROLLED_BUG_REPORT);
  // Every change of recording goes through replaceRecording, which revokes the
  // previous preview's object URL; the effect revokes the last one on unmount.
  const recordingPreviewUrl = useRef("");
  const replaceRecording = (next: Recording | null) => {
    if (recordingPreviewUrl.current) URL.revokeObjectURL(recordingPreviewUrl.current);
    recordingPreviewUrl.current = next?.previewUrl ?? "";
    setRecording(next);
  };
  useEffect(
    () => () => {
      fileGeneration.current += 1;
      if (recordingPreviewUrl.current) URL.revokeObjectURL(recordingPreviewUrl.current);
      recordingPreviewUrl.current = "";
    },
    [],
  );

  const isLocal = environment !== "s101" && environment !== "production";
  const isReady =
    title.trim().length >= 8 &&
    actualBehavior.trim().length >= 20 &&
    expectedBehavior.trim().length >= 10 &&
    reproductionSteps.trim().length >= 10 &&
    screenshot !== null &&
    confirmed;

  const reset = () => {
    submissionGeneration.current += 1;
    fileGeneration.current += 1;
    setTitle("");
    setActualBehavior("");
    setExpectedBehavior("");
    setReproductionSteps("");
    setScreenshot(null);
    replaceRecording(null);
    setConfirmed(false);
    setFileError("");
    setRecordingError("");
    setSubmitError("");
    setResult(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && loading) return;
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    const generation = fileGeneration.current;
    setFileError("");
    try {
      const next = await readScreenshot(file);
      if (generation !== fileGeneration.current) return;
      setScreenshot(next);
    } catch (error) {
      if (generation !== fileGeneration.current) return;
      const reason = errorReason(error);
      setScreenshot(null);
      setFileError(
        reason === "size"
          ? t(msg`Choose an image smaller than 5 MB.`)
          : reason === "type"
            ? t(msg`Choose a PNG, JPEG, or WebP image.`)
            : t(msg`We couldn't read that image. Try another file.`),
      );
    }
  };

  const handleRecording = async (file?: File) => {
    if (!file) return;
    const generation = fileGeneration.current;
    setRecordingError("");
    try {
      const next = await readRecording(file);
      if (generation !== fileGeneration.current) return;
      // An object URL, not the data URL: a 15 MB video would be a 20 MB string.
      replaceRecording({ ...next, previewUrl: URL.createObjectURL(file) });
    } catch (error) {
      if (generation !== fileGeneration.current) return;
      const reason = errorReason(error);
      replaceRecording(null);
      setRecordingError(
        reason === "size"
          ? t(msg`Choose a video smaller than 15 MB.`)
          : reason === "type"
            ? t(msg`Choose an MP4 or WebM video.`)
            : t(msg`We couldn't read that video. Try another file.`),
      );
    }
  };

  const handleSubmit = async (createAnyway = false) => {
    if (!isReady || !screenshot) return;
    const generation = ++submissionGeneration.current;
    setSubmitError("");
    try {
      const response = await submitReport({
        variables: {
          input: {
            mindspaceId,
            pageUrl: window.location.href,
            title: title.trim(),
            actualBehavior: actualBehavior.trim(),
            expectedBehavior: expectedBehavior.trim(),
            reproductionSteps: reproductionSteps.trim(),
            screenshotBase64: screenshot.base64,
            screenshotContentType: screenshot.contentType,
            ...(recording
              ? { recordingBase64: recording.base64, recordingContentType: recording.contentType }
              : {}),
            stagingChecksConfirmed: true,
            createAnyway,
          },
        },
      });
      if (!response.data?.submitControlledBugReport) {
        throw new Error("No report result");
      }
      if (generation !== submissionGeneration.current) return;
      setResult(response.data.submitControlledBugReport);
    } catch (error) {
      if (generation !== submissionGeneration.current) return;
      console.error("Failed to submit controlled bug report", error);
      setSubmitError(
        t(msg`The report couldn't be sent. Your notes are still here so you can try again.`),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <BugIcon className="size-6 text-primary" aria-hidden="true" />
            <DialogTitle>
              <Trans>Report a product bug</Trans>
            </DialogTitle>
            <Badge variant={isLocal ? "secondary" : environment === "s101" ? "outline" : "destructive"}>
              {isLocal ? <Trans>Local preview</Trans> : environment === "s101" ? <Trans>S101 only</Trans> : <Trans>Production</Trans>}
            </Badge>
          </div>
          <DialogDescription>
            <Trans>Share a reproducible defect with the engineering team. This is separate from product feedback or support.</Trans>
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <Alert>
            <BugIcon className="size-4" />
            <AlertTitle>
              {result.status === "LOCAL_PREVIEW" ? (
                <Trans>Local check complete</Trans>
              ) : result.status === "POSSIBLE_DUPLICATE" ? (
                <Trans>Possible duplicate found</Trans>
              ) : result.status === "LINEAR_ONLY" ? (
                <Trans>Created in Linear; Slack needs attention</Trans>
              ) : (
                <Trans>Bug reported</Trans>
              )}
            </AlertTitle>
            <AlertDescription>
              {result.status === "LOCAL_PREVIEW" ? (
                <Trans>No Linear issue or Slack message was created from your local environment.</Trans>
              ) : result.status === "POSSIBLE_DUPLICATE" ? (
                <>
                  <Trans>Review the existing issue before creating another:</Trans>{" "}
                  <a className="underline" href={result.issueUrl} target="_blank" rel="noreferrer">
                    {result.issueIdentifier}: {result.duplicateTitle}
                  </a>
                  .
                </>
              ) : (
                <>
                  <Trans>Your evidence was saved in</Trans>{" "}
                  <a className="underline" href={result.issueUrl} target="_blank" rel="noreferrer">
                    {result.issueIdentifier}
                  </a>
                  .
                </>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-5">
            {isLocal ? (
              <Alert>
                <ShieldAlertIcon className="size-4" />
                <AlertTitle><Trans>Local reports never leave this environment</Trans></AlertTitle>
                <AlertDescription><Trans>You can test this form, but it will not create a Linear issue or notify Slack.</Trans></AlertDescription>
              </Alert>
            ) : environment === "s101" ? (
              <Alert>
                <ShieldAlertIcon className="size-4" />
                <AlertTitle><Trans>This report will be labelled S101 only</Trans></AlertTitle>
                <AlertDescription><Trans>Check whether test data, permissions, delayed jobs, or a disabled staging integration explains the behaviour before submitting.</Trans></AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="bug-title"><Trans>Short summary</Trans></Label>
              <Input id="bug-title" aria-describedby="bug-title-help" value={title} maxLength={160} onChange={(event) => setTitle(event.target.value)} placeholder={t(msg`What is broken?`)} />
              <p id="bug-title-help" className="text-xs text-muted-foreground"><Trans>At least 8 characters.</Trans></p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-actual"><Trans>What happened?</Trans></Label>
              <Textarea id="bug-actual" aria-describedby="bug-actual-help" value={actualBehavior} maxLength={4000} rows={3} onChange={(event) => setActualBehavior(event.target.value)} />
              <p id="bug-actual-help" className="text-xs text-muted-foreground"><Trans>At least 20 characters.</Trans></p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-expected"><Trans>What did you expect?</Trans></Label>
              <Textarea id="bug-expected" aria-describedby="bug-expected-help" value={expectedBehavior} maxLength={4000} rows={3} onChange={(event) => setExpectedBehavior(event.target.value)} />
              <p id="bug-expected-help" className="text-xs text-muted-foreground"><Trans>At least 10 characters.</Trans></p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-steps"><Trans>Steps to reproduce</Trans></Label>
              <Textarea id="bug-steps" aria-describedby="bug-steps-help" value={reproductionSteps} maxLength={6000} rows={4} onChange={(event) => setReproductionSteps(event.target.value)} placeholder={t(msg`1. Open… 2. Select… 3. See…`)} />
              <p id="bug-steps-help" className="text-xs text-muted-foreground"><Trans>At least 10 characters.</Trans></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-screenshot"><Trans>Screenshot evidence</Trans></Label>
              <p className="text-sm text-muted-foreground"><Trans>Required. Remove personal or confidential information before attaching.</Trans></p>
              {screenshot ? (
                <div className="rounded-lg border bg-muted/30 p-3">
                  <img src={screenshot.previewUrl} alt={t(msg`Screenshot preview`)} className="max-h-56 w-full rounded object-contain" />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="truncate text-sm">{screenshot.name}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setScreenshot(null)}>
                      <XIcon className="mr-1 size-4" />
                      <Trans>Remove</Trans>
                    </Button>
                  </div>
                </div>
              ) : (
                <Label htmlFor="bug-screenshot" className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-sm hover:bg-muted/50">
                  <ImageIcon className="size-5" />
                  <Trans>Choose screenshot</Trans>
                </Label>
              )}
              <Input
                id="bug-screenshot"
                className="sr-only"
                type="file"
                accept={ALLOWED_SCREENSHOT_TYPES.join(",")}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = "";
                  void handleFile(file);
                }}
              />
              {fileError && <p className="text-sm text-destructive" role="alert">{fileError}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-recording"><Trans>Screen recording</Trans></Label>
              <p className="text-sm text-muted-foreground"><Trans>Optional. An MP4 or WebM video up to 15 MB (about 30 seconds).</Trans></p>
              {recording ? (
                <div className="rounded-lg border bg-muted/30 p-3">
                  <video src={recording.previewUrl} controls aria-label={t(msg`Recording preview`)} className="max-h-56 w-full rounded object-contain" />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="truncate text-sm">{recording.name}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => replaceRecording(null)}>
                      <XIcon className="mr-1 size-4" />
                      <Trans>Remove</Trans>
                    </Button>
                  </div>
                </div>
              ) : (
                <Label htmlFor="bug-recording" className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-sm hover:bg-muted/50">
                  <VideoIcon className="size-5" />
                  <Trans>Choose recording</Trans>
                </Label>
              )}
              <Input
                id="bug-recording"
                className="sr-only"
                type="file"
                accept={ALLOWED_RECORDING_TYPES.join(",")}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = "";
                  void handleRecording(file);
                }}
              />
              {recordingError && <p className="text-sm text-destructive" role="alert">{recordingError}</p>}
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-3">
              <Checkbox id="bug-confirmed" checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} />
              <Label htmlFor="bug-confirmed" className="font-normal leading-5">
                <Trans>I reproduced this after refreshing and checked that it is not explained by test data, my permissions, a delayed job, or an unavailable staging integration.</Trans>
              </Label>
            </div>

            {submitError && <p className="text-sm text-destructive" role="alert">{submitError}</p>}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" disabled={loading} onClick={() => handleOpenChange(false)}>
            {result ? <Trans>Close</Trans> : <Trans>Cancel</Trans>}
          </Button>
          {result?.status === "POSSIBLE_DUPLICATE" ? (
            <Button type="button" disabled={loading} onClick={() => void handleSubmit(true)}>
              {loading ? <Trans>Creating…</Trans> : <Trans>Create anyway</Trans>}
            </Button>
          ) : !result ? (
            <Button type="button" disabled={!isReady || loading} onClick={() => void handleSubmit()}>
              {loading ? <Trans>Sending…</Trans> : isLocal ? <Trans>Check local report</Trans> : <Trans>Create bug report</Trans>}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
